import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import Button from '../../components/ui/Button';
import Footer from '../homepage/components/Footer';
import Header from '../../components/ui/Header';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import { publicationsService } from '../../services/publicationsService';

const Publications = () => {
	const [publications, setPublications] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedPublication, setSelectedPublication] = useState(null);

	// Load publications from database
	useEffect(() => {
		const loadPublications = async () => {
			try {
				setLoading(true);
				const { data, error } = await publicationsService.getPublications();

				if (error) {
					setError(error);
					return;
				}

				setPublications(data || []);
			} catch (err) {
				setError('Failed to load publications');
				console.error('Error loading publications:', err);
			} finally {
				setLoading(false);
			}
		};

		loadPublications();
	}, []);

	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	};

	// Loading state
	if (loading) {
		return (
			<>
				<Helmet>
					<title>Publications - FEPA Law Partners</title>
					<meta
						name='description'
						content='Legal publications and articles by FEPA Law Partners attorneys'
					/>
					<meta
						name='keywords'
						content='legal publications, law articles, legal insights, FEPA Law Partners'
					/>
				</Helmet>
				<Header />
				<div className='min-h-screen bg-gray-50 flex items-center justify-center'>
					<div className='text-center'>
						<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#800020] mx-auto mb-4'></div>
						<p className='text-gray-600'>Loading publications...</p>
					</div>
				</div>
				<Footer />
			</>
		);
	}

	// Error state
	if (error || publications.length === 0) {
		return (
			<>
				<Helmet>
					<title>Publications - FEPA Law Partners</title>
					<meta
						name='description'
						content='Legal publications and articles by FEPA Law Partners attorneys'
					/>
					<meta
						name='keywords'
						content='legal publications, law articles, legal insights, FEPA Law Partners'
					/>
				</Helmet>
				<Header />
				<div className='min-h-screen bg-gray-50 flex items-center justify-center'>
					<div className='text-center'>
						<Icon name='AlertCircle' size={48} className='mx-auto mb-4 text-red-500' />
						<h3 className='text-gray-900 text-xl mb-2'>Error Loading Publications</h3>
						<p className='text-gray-600'>{error?.message || 'No publications available'}</p>
					</div>
				</div>
				<Footer />
			</>
		);
	}

	return (
		<>
			<Helmet>
				<title>Publications - Fepa Law Partners | Legal Articles & Insights</title>
				<meta
					name='description'
					content='Stay informed with Fepa Law Partners latest publications, articles, and legal insights. Expert commentary on Nigerian legal trends and developments.'
				/>
				<meta
					name='keywords'
					content='legal publications Nigeria, articles, legal insights, case studies, Nigerian law, legal analysis, law firm publications'
				/>
			</Helmet>

			<div className='min-h-screen bg-gray-50'>
				<Header />

				{/* Hero Section */}
				<section className='relative bg-gradient-to-r from-[#800020] to-[#800020]/80 pt-20 pb-16'>
					<div className='absolute inset-0 bg-black/20'></div>
					<div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
						<div className='text-center'>
							<div className='inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6'>
								<Icon name='BookOpen' size={20} color='white' />
								<span className='text-white font-source font-medium'>
									Legal Insights & Analysis
								</span>
							</div>
							<h1 className='font-playfair font-bold text-4xl lg:text-6xl text-white mb-6'>
								Publications &<span className='block text-[#DB954E]'>Insights</span>
							</h1>
							<p className='text-xl text-white/90 max-w-3xl mx-auto leading-relaxed'>
								Stay informed with our expert analysis of current legal trends, landmark cases, and
								insights from our experienced legal professionals.
							</p>
						</div>
					</div>
				</section>

				{/* Publications Section */}
				<section className='py-16 bg-white'>
					<div className='max-w-6xl mx-auto px-4 lg:px-8'>
						{/* Publications Grid */}
						<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
							{publications?.map((publication) => (
								<article
									key={publication?.id}
									className='bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100'
								>
									{/* Article Header */}
									<div className='p-6'>
										<div className='flex items-center justify-between mb-3'>
											<span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#800020]/10 text-[#800020]'>
												{publication?.publication_type}
											</span>
											<span className='text-sm text-gray-500'>
												{publication?.read_time_minutes} min read
											</span>
										</div>

										{/* Title */}
										<h3 className='font-playfair font-bold text-xl text-[#800020] mb-3 line-clamp-2'>
											{publication?.title}
										</h3>

										{/* Excerpt */}
										<p className='text-gray-700 font-source text-sm leading-relaxed mb-4 line-clamp-3'>
											{publication?.excerpt}
										</p>

										{/* Author and Date */}
										<div className='flex items-center justify-between text-sm text-gray-600'>
											<div className='flex items-center space-x-2'>
												<Icon name='User' size={16} />
												<span>{publication?.author_name}</span>
											</div>
											<div className='flex items-center space-x-2'>
												<Icon name='Calendar' size={16} />
												<span>{formatDate(publication?.publication_date)}</span>
											</div>
										</div>
									</div>

									{/* Action Buttons */}
									<div className='px-6 pb-6 space-y-2'>
										{/* Read More Button - Links to external URL from database */}
										{publication?.external_url ? (
											<a
												href={publication.external_url}
												target='_blank'
												rel='noopener noreferrer'
												className='w-full bg-[#800020] hover:bg-[#800020]/90 text-white px-4 py-2 rounded-lg font-source font-semibold text-sm transition-colors duration-200 inline-block text-center'
											>
												Read More
											</a>
										) : (
											<button
												className='w-full bg-gray-400 text-white px-4 py-2 rounded-lg font-source font-semibold text-sm cursor-not-allowed'
												disabled
											>
												No External Link
											</button>
										)}

										{/* View Full Article Button - Shows full content */}
										{publication?.content && (
											<button
												onClick={() => setSelectedPublication(publication)}
												className='w-full bg-[#DB954E] hover:bg-[#DB954E]/90 text-white px-4 py-2 rounded-lg font-source font-semibold text-sm transition-colors duration-200'
											>
												View Full Article
											</button>
										)}
									</div>
								</article>
							))}
						</div>
					</div>
				</section>

				{/* Call to Action */}
				<section className='py-16 bg-gradient-to-r from-[#800020] to-[#800020]/90 text-white'>
					<div className='max-w-4xl mx-auto px-4 lg:px-8 text-center'>
						<h2 className='text-3xl md:text-4xl font-playfair font-bold mb-4'>Stay Informed</h2>
						<p className='text-lg text-white/90 mb-8 max-w-2xl mx-auto'>
							Subscribe to our newsletter for the latest legal analysis, case studies, and expert
							commentary delivered to your inbox.
						</p>
						<div className='flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4'>
							<a
								href='mailto:info@fepalawpartners.com?subject=Newsletter Subscription'
								className='inline-flex items-center space-x-3 bg-[#DB954E] hover:bg-[#DB954E]/90 text-white px-8 py-4 rounded-lg font-source font-semibold shadow-lg hover:shadow-xl transition-all duration-300'
							>
								<Icon name='Mail' size={18} />
								<span>Subscribe to Newsletter</span>
							</a>

							<a
								href='tel:+2349095529557'
								className='inline-flex items-center space-x-3 border-2 border-white text-white hover:bg-white hover:text-[#800020] px-8 py-4 rounded-lg font-source font-semibold transition-all duration-300'
							>
								<Icon name='Phone' size={18} />
								<span>Contact Us</span>
							</a>
						</div>
					</div>
				</section>

				<Footer />
			</div>

			{/* Full Article Modal */}
			<AnimatePresence>
				{selectedPublication && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'
						onClick={() => setSelectedPublication(null)}
					>
						<motion.div
							initial={{ scale: 0.9, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							exit={{ scale: 0.9, opacity: 0 }}
							className='bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden'
							onClick={(e) => e.stopPropagation()}
						>
							{/* Modal Header */}
							<div className='bg-[#800020] text-white p-6'>
								<div className='flex items-center justify-between'>
									<div>
										<h2 className='text-2xl font-playfair font-bold mb-2'>
											{selectedPublication.title}
										</h2>
										<div className='flex items-center space-x-4 text-sm opacity-90'>
											<div className='flex items-center space-x-1'>
												<Icon name='User' size={16} />
												<span>{selectedPublication.author_name}</span>
											</div>
											<div className='flex items-center space-x-1'>
												<Icon name='Calendar' size={16} />
												<span>{formatDate(selectedPublication.publication_date)}</span>
											</div>
											<div className='flex items-center space-x-1'>
												<Icon name='Clock' size={16} />
												<span>{selectedPublication.read_time_minutes} min read</span>
											</div>
										</div>
									</div>
									<button
										onClick={() => setSelectedPublication(null)}
										className='text-white hover:text-gray-200 transition-colors'
									>
										<Icon name='X' size={24} />
									</button>
								</div>
							</div>

							{/* Modal Content */}
							<div className='p-6 overflow-y-auto max-h-[calc(90vh-120px)]'>
								{selectedPublication.excerpt && (
									<div className='mb-6 p-4 bg-gray-50 rounded-lg'>
										<p className='text-gray-700 font-source italic'>
											{selectedPublication.excerpt}
										</p>
									</div>
								)}

								<div className='prose prose-lg max-w-none'>
									<div className='whitespace-pre-wrap text-gray-700 font-source leading-relaxed'>
										{selectedPublication.content}
									</div>
								</div>
							</div>

							{/* Modal Footer */}
							<div className='bg-gray-50 px-6 py-4 flex justify-between items-center'>
								<div className='flex items-center space-x-2'>
									<span className='inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#800020]/10 text-[#800020]'>
										{selectedPublication.publication_type}
									</span>
								</div>
								<div className='flex space-x-3'>
									<button
										onClick={() => setSelectedPublication(null)}
										className='px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors'
									>
										Close
									</button>
									{selectedPublication?.external_url ? (
										<a
											href={selectedPublication.external_url}
											target='_blank'
											rel='noopener noreferrer'
											className='px-4 py-2 bg-[#800020] text-white rounded-md hover:bg-[#800020]/90 transition-colors'
										>
											Read More Online
										</a>
									) : (
										<button
											className='px-4 py-2 bg-gray-400 text-white rounded-md cursor-not-allowed'
											disabled
										>
											No External Link
										</button>
									)}
								</div>
							</div>
						</motion.div>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};

export default Publications;
